import { orderShorthandsFirst } from "./css";
import { elementSchemaOf } from "./schema";
import type { Decoration, DecorationCss } from "./types";

export interface DecorationPseudoRule {
	selector: string;
	css: DecorationCss;
}

const PSEUDOS = ["before", "after"] as const;

const STYLE_ID = "inno-decoration-pseudo";

const IMPORTANT = /\s*!important\s*$/i;

const INSERT_LAYER = /^(ins_\d{13})_([a-z0-9_]+)$/;

const quoted = (value: string) => `"${value.replace(/["\\]/g, "\\$&")}"`;

const layerOf = (screen: string, key: string): [string, string] | null => {
	const insert = key.match(INSERT_LAYER);
	if (insert) return [insert[1], insert[2]];
	if (elementSchemaOf(screen, key)) return null;
	for (let split = key.lastIndexOf("_"); split > 0; split = key.lastIndexOf("_", split - 1)) {
		const name = key.slice(0, split);
		const state = key.slice(split + 1);
		if (elementSchemaOf(screen, name)?.css?.includes(state)) return [name, state];
	}
	return null;
};

const targetOf = (screen: string, key: string) => {
	const layer = layerOf(screen, key);
	const base = `[data-screen=${quoted(screen)}][data-el=${quoted(layer ? layer[0] : key)}]`;
	return layer ? `${base}[data-decor-state~=${quoted(layer[1])}]` : base;
};

export const pseudoRulesOf = (decoration: Decoration | null | undefined): DecorationPseudoRule[] =>
	Object.entries(decoration?.screens ?? {}).flatMap(([screen, elements]) =>
		Object.entries(elements).flatMap(([key, element]) =>
			PSEUDOS.flatMap((pseudo) => {
				const css = element[pseudo];
				return css && Object.keys(css).length > 0 ? [{ selector: `${targetOf(screen, key)}::${pseudo}`, css }] : [];
			}),
		),
	);

export const applyPseudoRules = (rules: DecorationPseudoRule[]) => {
	let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
	if (rules.length === 0) {
		style?.remove();
		return;
	}
	if (!style) {
		style = document.createElement("style");
		style.id = STYLE_ID;
	}
	document.head.appendChild(style);
	const sheet = style.sheet;
	if (!sheet) return;

	while (sheet.cssRules.length > 0) sheet.deleteRule(0);
	for (const { selector, css } of rules) {
		let index: number;
		try {
			index = sheet.insertRule(`${selector} {}`, sheet.cssRules.length);
		} catch {
			continue;
		}
		const rule = sheet.cssRules[index] as CSSStyleRule;
		for (const [prop, value] of Object.entries(orderShorthandsFirst(css) ?? {})) rule.style.setProperty(prop, value.replace(IMPORTANT, ""), "important");
	}
};
